import {
  OpeningDay,
  OpeningTimes,
  TimePoint,
  TimeRange,
  Weekday,
  weekDays,
} from '../../../../lead.model';

export function copy(openingTimes: OpeningTimes): OpeningTimes {
  return JSON.parse(JSON.stringify(openingTimes));
}

export function timeRangeIsWholeDay(timeRange: TimeRange[]): boolean {
  return (
    JSON.stringify(timeRange) ===
    JSON.stringify([
      {
        start: { hour: 0, minute: 0, second: 0 },
        end: { hour: 24, minute: 0, second: 0 },
      },
    ])
  );
}

export function mergeOpeningTimes(
  openingTimesA: OpeningTimes,
  openingTimesB: OpeningTimes,
): OpeningTimes {
  const secondsIntervalA = openingTimesToSeconds(openingTimesA);
  const secondsIntervalB = openingTimesToSeconds(openingTimesB);
  return secondsIntervalToOpeningTimes(
    mergeTimeIntervals(secondsIntervalA, secondsIntervalB),
  );
}

//from here on are private declarations

interface SecondsInterval {
  start: number;
  end: number;
}

function mergeTimeIntervals(
  secondsIntervals: SecondsInterval[],
  secondsIntervalsToAdd: SecondsInterval[],
): SecondsInterval[] {
  return reduceTimeInterval(
    [...secondsIntervals, ...secondsIntervalsToAdd].sort(
      (x, y) => x.start - y.start,
    ),
  );
}

function reduceTimeInterval(
  secondsTimeIntervals: SecondsInterval[],
): SecondsInterval[] {
  return secondsTimeIntervals.reduce(
    (merged: SecondsInterval[], curr: SecondsInterval) => {
      if (merged.length > 0) {
        const last = merged[merged.length - 1];
        if (curr.start <= last.end) {
          last.end = Math.max(curr.end, last.end);
        } else {
          merged.push(curr);
        }
        return merged;
      } else {
        return [curr];
      }
    },
    [] as SecondsInterval[],
  );
}

function secondsIntervalToOpeningTimes(secondsIntervals: SecondsInterval[]) {
  const openingTimes: OpeningTimes = { openingDays: [] };

  for (const secondsInterval of secondsIntervals) {
    const weekDayStart: Weekday = getWeekDayFromSecond(secondsInterval.start);
    const timePointStart: TimePoint = getTimePointFromSecond(
      secondsInterval.start,
    );
    let timePointEnd: TimePoint = getTimePointFromSecond(secondsInterval.end);

    const weekDayEnd: Weekday = getWeekDayFromSecond(secondsInterval.end);

    if (secondsInterval.end - secondsInterval.start < 86400) {
      if (secondsInterval.end % 86400 === 0) {
        timePointEnd = { hour: 24, minute: 0 };
      }
      addOpeningTime(openingTimes, weekDayStart, {
        start: timePointStart,
        end: timePointEnd,
      });
    } else {
      const indexStart = weekDays.indexOf(weekDayStart);
      let indexEnd = weekDays.indexOf(weekDayEnd);
      if (indexEnd === -1) {
        indexEnd = 7;
      }
      const timePointEndOfDay: TimePoint = { hour: 24, minute: 0 };
      const timePointStartOfDay: TimePoint = {
        hour: 0,
        minute: 0,
      };
      addOpeningTime(openingTimes, weekDayStart, {
        start: timePointStart,
        end: timePointEndOfDay,
      });
      for (let i = indexStart + 1; i < indexEnd; i++) {
        addOpeningTime(openingTimes, weekDays[i], {
          start: timePointStartOfDay,
          end: timePointEndOfDay,
        });
      }

      if (timePointToSeconds(timePointEnd) > 0) {
        addOpeningTime(openingTimes, weekDayEnd, {
          start: timePointStartOfDay,
          end: timePointEnd,
        });
      }
    }
  }
  return openingTimes;
}

function addOpeningTime(
  openingTimes: OpeningTimes,
  weekDay: Weekday,
  timeRange: TimeRange,
) {
  const openingDay: OpeningDay | undefined = openingTimes.openingDays.find(
    (openingDay: OpeningDay) => openingDay.weekday === weekDay,
  );
  if (openingDay) {
    openingDay.timeRange.push(timeRange);
  } else {
    openingTimes.openingDays.push({
      weekday: weekDay,
      timeRange: [timeRange],
    });
  }
}

function getTimePointFromSecond(seconds: number): TimePoint {
  const hour = Math.floor(seconds / 3600) % 24;
  const minute = Math.floor(seconds / 60) % 60;
  return { hour: hour, minute: minute };
}

function getWeekDayFromSecond(second: number): Weekday {
  return weekDays[Math.floor(second / 86400)];
}

function openingTimesToSeconds(openingTimes: OpeningTimes): SecondsInterval[] {
  const secondsIntervals: SecondsInterval[] = [];
  const sunday: OpeningDay | undefined = openingTimes.openingDays.find(
    (openingDay: OpeningDay) => openingDay.weekday === 'SUNDAY',
  );
  if (sunday) {
    for (const timeRange of sunday.timeRange) {
      if (
        timePointToSeconds(timeRange.end) <= timePointToSeconds(timeRange.start)
      ) {
        secondsIntervals.push({
          start: 0,
          end: timePointToSeconds(timeRange.end),
        });
      }
    }
  }

  for (const openingDay of openingTimes.openingDays) {
    for (const timeRange of openingDay.timeRange) {
      if (
        openingDay.weekday === 'SUNDAY' &&
        timePointToSeconds(timeRange.end) <= timePointToSeconds(timeRange.start)
      ) {
        const base = 6 * 86400;
        secondsIntervals.push({
          start: base + timePointToSeconds(timeRange.start),
          end: 7 * 86400,
        });
      } else {
        secondsIntervals.push(
          timeRangeToSeconds(openingDay.weekday, timeRange),
        );
      }
    }
  }
  return reduceTimeInterval(secondsIntervals.sort((x, y) => x.start - y.start));
}

function timeRangeToSeconds(
  weekDay: Weekday,
  timeRange: TimeRange,
): SecondsInterval {
  const base = weekDayToSeconds(weekDay);
  const start = base + timePointToSeconds(timeRange.start);
  let end = base + timePointToSeconds(timeRange.end);

  if (end <= start) {
    end += 86400;
  }
  return { start: start, end: end };
}

function weekDayToSeconds(weekDay: Weekday): number {
  return weekDays.indexOf(weekDay) * 86400;
}

function timePointToSeconds(timePoint: TimePoint): number {
  return (timePoint.hour * 60 + timePoint.minute) * 60;
}
